'use client';

import { useId } from 'react';

interface VioFigureProps {
  /** Rendered width in px; height follows the figure's fixed aspect ratio. */
  size?: number;
  className?: string;
  animate?: boolean;
}

const VIEW_W = 160;
const VIEW_H = 236;

/**
 * Vio's full-body illustration — for moments that can afford real
 * space: an empty assistant thread, onboarding, a finished analysis.
 * For compact contexts (launcher, message avatar) use `VioAvatar`.
 */
export function VioFigure({ size = 160, className, animate = true }: VioFigureProps) {
  const uid = useId();
  const bodyGrad = `vio-body-${uid}`;
  const visorGrad = `vio-visor-${uid}`;
  const tipGrad = `vio-tip-${uid}`;

  return (
    <svg
      aria-hidden
      className={className}
      height={(size * VIEW_H) / VIEW_W}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width={size}
    >
      <defs>
        <radialGradient cx="34%" cy="16%" id={bodyGrad} r="100%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="55%" stopColor="#F8F6F0" />
          <stop offset="100%" stopColor="#EDEAE0" />
        </radialGradient>
        <linearGradient id={visorGrad} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#E7BE94" />
          <stop offset="55%" stopColor="#B86E2C" />
          <stop offset="100%" stopColor="#8F5420" />
        </linearGradient>
        <radialGradient cx="35%" cy="30%" id={tipGrad} r="80%">
          <stop offset="0%" stopColor="#ECC9A8" />
          <stop offset="100%" stopColor="#B86E2C" />
        </radialGradient>
      </defs>

      <ellipse cx="80" cy="228" fill="#131210" opacity="0.07" rx="46" ry="8" />
      <ellipse cx="80" cy="228" fill="#131210" opacity="0.06" rx="30" ry="5" />

      <line stroke="#B86E2C" strokeLinecap="round" strokeWidth="3" x1="80" x2="80" y1="15" y2="30" />
      <circle cx="80" cy="10" fill={`url(#${tipGrad})`} opacity="0.9" r="5.5" />

      <rect
        fill={`url(#${bodyGrad})`}
        height="118"
        rx="40"
        stroke="#DDD8C9"
        strokeOpacity="0.7"
        strokeWidth="1"
        width="88"
        x="36"
        y="108"
      />
      <rect
        fill={`url(#${bodyGrad})`}
        height="100"
        rx="50"
        stroke="#DDD8C9"
        strokeOpacity="0.7"
        strokeWidth="1"
        width="112"
        x="24"
        y="26"
      />

      <rect fill={`url(#${visorGrad})`} height="26" rx="13" width="72" x="44" y="64" />
      <rect fill="#FFFFFF" height="8" opacity="0.18" rx="4" width="30" x="49" y="67" />
      <circle className={animate ? 'vio-light' : undefined} cx="64" cy="77" fill="#FBFAF6" r="4.3" />
      <circle
        className={animate ? 'vio-light' : undefined}
        cx="96"
        cy="77"
        fill="#FBFAF6"
        r="4.3"
        style={animate ? { animationDelay: '0.5s' } : undefined}
      />

      <circle cx="80" cy="168" fill="#131210" opacity="0.045" r="18" />
      <circle cx="80" cy="167" fill="#F2EFE7" r="17" stroke="#DDD8C9" strokeWidth="1" />
      <text
        fill="#8F5420"
        fontFamily="'Fraunces', 'Source Serif 4', serif"
        fontSize="19"
        fontStyle="italic"
        textAnchor="middle"
        x="80"
        y="175"
      >
        v
      </text>

      <line
        stroke="#131210"
        strokeLinecap="round"
        strokeOpacity="0.05"
        strokeWidth="2"
        x1="52"
        x2="108"
        y1="212"
        y2="212"
      />
    </svg>
  );
}
